import { useEffect, useState } from 'react';
import { Modal, Button } from '../../../shared/components';
import { LccRole } from '../../../shared/auth/types';
import { ASSIGNABLE_MODULES, ROLE_DEFAULT_MODULES, ModuleDefinition } from '../../../shared/config/modules';

interface AssignableUser {
  id: string;
  fullName: string;
  role: LccRole;
  allowedModules: string[] | null;
}

interface AssignModulesModalProps {
  user: AssignableUser | null;
  onClose: () => void;
  onSave: (id: string, allowedModules: string[] | null) => Promise<void>;
  /** Catalogo de pantallas asignables - por defecto el de Control Operativo. Gestion Humana pasa el suyo (ver shared/config/hrModules.ts). */
  assignableModules?: ModuleDefinition[];
  /** Pantallas por defecto de cada rol - por defecto las de Control Operativo. */
  roleDefaultModules?: Record<LccRole, readonly string[]>;
}

/**
 * "Pantallas permitidas": override individual sobre las pantallas por defecto
 * del Rol del usuario. Vacio = usa las pantallas por defecto de su Rol.
 */
export function AssignModulesModal({ user, onClose, onSave, assignableModules = ASSIGNABLE_MODULES, roleDefaultModules = ROLE_DEFAULT_MODULES }: AssignModulesModalProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) setSelected(user.allowedModules ?? []);
  }, [user]);

  if (!user) return null;

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(user.id, selected.length > 0 ? selected : null);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const defaultsForRole = roleDefaultModules[user.role].map((id) => assignableModules.find((m) => m.id === id)?.label).filter(Boolean).join(', ');

  return (
    <Modal open={!!user} onClose={onClose} title="Asignar pantallas">
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          Usuario: <span className="font-semibold">{user.fullName}</span>
        </p>
        <p className="text-xs text-ink-400">
          Selecciona las pantallas a las que tendrá acceso. Sin selección = usa las pantallas por defecto de su rol ({user.role}): {defaultsForRole || '—'}.
        </p>
        <div className="flex flex-wrap gap-2">
          {assignableModules.map((mod) => {
            const active = selected.includes(mod.id);
            return (
              <button
                key={mod.id}
                onClick={() => toggle(mod.id)}
                className={[
                  'flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                  active ? 'border-brand-500 bg-brand-light text-brand-700' : 'border-ink-200 bg-surface text-ink-600 hover:bg-ink-50',
                ].join(' ')}
              >
                <mod.icon width={16} height={16} />
                {mod.label}
              </button>
            );
          })}
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" fullWidth onClick={onClose}>Cancelar</Button>
          <Button fullWidth onClick={handleSave} isLoading={isSaving}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}
